import { useContainerBlockContext, useCustomViewBlockContext } from '@lighthouse/block'
import { OnOffBlock } from '@lighthouse/block/src/blocks'
import type { ActorEvent, OnOffBlockAbstract } from '@lighthouse/core'
import { deleteBlockValueAtLeaf, getWithScopeId, setBlockValueAtLeaf, useAtomAction } from '@lighthouse/shared'
import React, { useCallback, useEffect } from 'react'

import { pageStackOfBlockAtom } from '@/atoms/application/state'
import { useCurrentStackIdContext } from '@/contexts/PageContext'
import { useActionTrigger } from '@/hooks/useActionTrigger'

interface OnOffBlockProps {
    blockData: OnOffBlockAbstract
}

const OnOffBlockController: React.FC<OnOffBlockProps> = ({ blockData }) => {
    const { handleActionListTrigger } = useActionTrigger(blockData.id)
    const { record } = useCustomViewBlockContext()
    const { scope } = useContainerBlockContext()
    const stackId = useCurrentStackIdContext()
    const { run: setPageStackOfBlockAtom } = useAtomAction(pageStackOfBlockAtom)

    const blockKey = getWithScopeId(blockData.id, scope)
    const recordId = record?.id
    const handleTriggerEvent = useCallback(
        (id: string, actorEvent: ActorEvent) => {
            return handleActionListTrigger(id, actorEvent, { viewRecord: record })
        },
        [handleActionListTrigger, record]
    )

    const handleValueChange = useCallback(
        (checked: boolean) => {
            setPageStackOfBlockAtom(draft => {
                setBlockValueAtLeaf(
                    draft,
                    { stackId, blockId: blockKey, recordId },
                    { id: blockKey, blockType: 'onOff', scope, value: checked }
                )
            })
        },
        [blockKey, recordId, scope, setPageStackOfBlockAtom, stackId]
    )

    useEffect(() => {
        // init with defaultChecked
        handleValueChange(!!blockData.config.defaultChecked)
        return () => {
            setPageStackOfBlockAtom(draft => {
                deleteBlockValueAtLeaf(draft, { stackId, blockId: blockKey, recordId })
            })
        }
    }, [blockData.config.defaultChecked, handleValueChange, blockKey, recordId, setPageStackOfBlockAtom, stackId])

    return <OnOffBlock onTriggerEvent={handleTriggerEvent} onValueChange={handleValueChange} blockData={blockData} />
}

export default OnOffBlockController
